// src/components/order/order-form.tsx
import {
  Alert,
  Avatar,
  Box,
  Button,
  Flex,
  Grid,
  Group,
  Input,
  Modal,
  Paper,
  Radio,
  Select,
  SimpleGrid,
  Text,
  TextInput,
  Title,
  UnstyledButton,
  SegmentedControl,
} from "@mantine/core"
import { DatePickerInput } from "@mantine/dates"
import classes from "./order-form.module.css"
import { useForm } from "@mantine/form"
import { PaperPlaneTiltIcon, PercentIcon, MagnifyingGlassIcon, UserIcon, XIcon } from "@phosphor-icons/react";
import dayjs from "dayjs"
import { yupResolver } from "mantine-form-yup-resolver"
import React, { ReactNode, useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router"
import { useTranslation } from "react-i18next"
import { DisabledReason, FormChecklist, HelpButtonGroup, HelpIcon, helpText, nextStepToast, useChecklistMissing, useHelpContext } from "@dehub/theme"
import { OrderApi } from "../../apis"
import { IBusiness } from "../../models/Business"
import { IAuth } from "../../store/Auth"
import { IGeneral } from "../../store/General"
import { findTermDiscount, termDiscountLabel } from "../../utils"
import { dateFormat, dateTimeSecFormat } from "../../utils/date"
import HttpHandler from "../../utils/http-handler"
import { message } from "../../utils/message"
import { renderName } from "../../utils/render-name"
import { SelectItemField } from "../form/select-item-field"
import { usePermission } from "../permission"
import { Breadcrumb } from "../ui/breadcrumb"
import { PermissionButton } from "../ui/permission-button"
import { StatusBadge } from "../ui/status-badge"
import { ReasonTooltip } from "../ui/reason-tooltip"
import { DotPill } from "../delivery/atoms"
import { translateBackendError } from "../delivery/translate-error"
import { OrderStatusPill } from "../buyer-sales/order-status-pill"

// Sub-components
import { SupplierSalesAddDynamicForm } from "../supplier-sales/add-dynamic-form"
import { BuyerSalesDynamicForm } from "../buyer-sales/dynamic-form"
import { PurchaseSalesDynamicDetailForm } from "../purchase-sales/dynamic-detail-form"
import { SupplierSalesDynamicReviewForm } from "../supplier-sales/review-form"
import { PurchaseSalesConfirmForm } from "../purchase-sales/confirm-form"

import { useVariantPricing } from "./use-variant-pricing"
import { VariantSelectorDrawer } from "../shared/variant-selector-drawer"

// Local imports
import { OrderFormProps, IOrderFormData } from "./types"
import { getValidationSchema } from "./validation"
import {
  useDeliveryDays,
  useNetworkData,
  useBusinessData,
  useStaffData,
  useBranchData,
  useReceiverBranch,
  useAvailableDeliveryDates,
} from "./hooks"
import { DetailActions } from "./detail-actions"

/** «CODE, Name» for a business; a single «-» when both are empty (never «-, -»). */
function joinCodeName(code?: string | null, name?: string | null): string {
  const parts = [code, name].filter((p): p is string => !!p)
  return parts.length > 0 ? parts.join(", ") : "-"
}

/** Canvas field: sentence-case grey label, bold value below. */
function FormField({ label, value }: { label: ReactNode; value?: ReactNode }) {
  return (
    <Box>
      <Text className={classes.fieldLabel}>{label}</Text>
      <Text className={classes.fieldValue}>{value == null || value === "" ? "-" : value}</Text>
    </Box>
  )
}

const FIELD_SPAN = { base: 12, sm: 6, md: 4 }

const getInitData = (): IOrderFormData => ({
  regNumber: undefined,
  partnerName: undefined,
  businessId: undefined,
  receiverBranchId: undefined,
  deliveryType: undefined,
  deliveryDate: undefined,
  receiverStaffId: undefined,
  partnerType: undefined,
  lines: [],
  senderNote: undefined,
  senderAdditionalNote: undefined,
  shippingAmount: undefined,
  discountType: undefined,
  discountValue: undefined,
  supplierId: undefined,
  attachments: [],
  toReview: false,
  send: false,
})

export function OrderForm({ mode, side, data, preselectedPartner, backPath, onSuccess, onCancel, hideLayout }: OrderFormProps) {
  const navigate = useNavigate()
  const { t } = useTranslation(["order", "common", "supplierSales"])

  // Redux state
  const { user } = useSelector((state: { auth: IAuth }) => state.auth)
  const { deliveryDateTypes, orderStatus } = useSelector((state: { general: IGeneral }) => state.general)

  // Local state
  const [loading, setLoading] = useState(false)
  const [active, setActive] = useState<number | string>("")
  const [modalAction, setModalAction] = useState<string | null>(null)

  // Hooks — businessData starts with preselectedPartner so the form opens
  // with a partner already populated when one was supplied by the caller
  const staticDays = useDeliveryDays()
  const { businessData, fetchBusiness } = useBusinessData(preselectedPartner ?? null)
  const { receiverBranch, fetchBranch } = useReceiverBranch()

  // Permissions
  const ORD_SAVE = usePermission("ORD_SAVE")
  const ORD_SEND = usePermission("ORD_SEND")
  const ORD_EDIT = usePermission("ORD_EDIT")
  const ORD_NET_NEW_ORDER = usePermission("ORD_NET_NEW_ORDER")

  // Network data
  const hasNetworkAccess = mode === "create" ? (ORD_SAVE.hasAccess || ORD_SEND.hasAccess) : ORD_NET_NEW_ORDER.hasAccess
  const { data: networkData } = useNetworkData(hasNetworkAccess)

  // Buyer side needs editable receiver staff/branch in BOTH create and edit
  const buyerReceiverFieldsEnabled = side === "buyer" && (mode === "create" || mode === "edit")
  const buyerReceiverHasAccess =
    mode === "edit" ? ORD_EDIT.hasAccess : (ORD_SAVE.hasAccess || ORD_SEND.hasAccess)

  const { data: staffData } = useStaffData(
    buyerReceiverFieldsEnabled ? user?.currentBusinessId ?? undefined : undefined,
    buyerReceiverHasAccess
  )
  const { data: branchData } = useBranchData(
    buyerReceiverFieldsEnabled ? user?.currentBusinessId ?? undefined : undefined,
    buyerReceiverHasAccess
  )

  // Computed values
  const isReadOnly = mode === "detail"
  // «Харилцагчаар захиалах»: the customer is fixed by the route, so the three search fields (business, tax id,
  // partner name) are not offered; the partner is shown read-only in the ledger below (Ord_CustomerOrder, O82).
  const partnerLocked = mode === "create" && !!preselectedPartner
  // Ord_CustomerOrder / Ord_SalesNew: supplier create is two columns (customer + delivery | lines, summary).
  const supplierCreate = side === "supplier" && mode === "create"
  const fieldSpan = supplierCreate ? { base: 12, sm: 6 } : FIELD_SPAN
  const searchSpan = supplierCreate ? 12 : FIELD_SPAN
  const showStaffBranchFields = buyerReceiverFieldsEnabled

  // Get status info
  const status = orderStatus?.find((s: any) => s.code === data?.orderStatus)

  // Get page config based on mode and side
  const getPageConfig = () => {
    // Create on supplier side from a contracted partner
    if (mode === "create" && side === "supplier" && preselectedPartner) {
      return {
        title: t("order:form.create.title"),
        subTitle: t("order:form.create.subTitle"),
        backPath: "/supplier-customer",
        breadcrumbs: [
          { label: t("order:breadcrumb.home"), path: "/" },
          { label: t("supplierSales:customer.title"), path: "/supplier-customer" },
          { label: t("order:form.create.title") },
        ],
      }
    }
    if (mode === "create" && side === "supplier") {
      return {
        title: t("order:form.create.title"),
        subTitle: t("order:form.create.subTitle"),
        backPath: "/supplier-sales",
        breadcrumbs: [
          { label: t("order:breadcrumb.home"), path: "/" },
          { label: t("order:breadcrumb.sales"), path: "/supplier-sales" },
          { label: t("order:form.create.title") },
        ],
      }
    }
    // Create on buyer side from a contracted partner
    if (mode === "create" && side === "buyer") {
      return {
        title: t("order:form.create.title"),
        // B44: the buyer registers a purchase order, not a sales order.
        subTitle: t("order:form.create.buyerSubTitle"),
        backPath: "/buyer-customer",
        breadcrumbs: [
          { label: t("order:breadcrumb.home"), path: "/" },
          { label: t("order:buyerCustomer.title"), path: "/buyer-customer" },
          { label: t("order:form.create.title") },
        ],
      }
    }
    if (mode === "edit" && side === "buyer") {
      return {
        title: t("order:form.edit.title"),
        subTitle: data?.purchaseCode || t("order:form.edit.subTitle"),
        backPath: "/buyer-sales",
        breadcrumbs: [
          { label: t("order:breadcrumb.home"), path: "/" },
          { label: t("order:breadcrumb.purchase"), path: "/buyer-sales" },
          { label: data?.purchaseCode || t("order:form.edit.title") },
        ],
      }
    }
    if (mode === "detail" && side === "supplier") {
      return {
        title: t("order:form.detail.title"),
        subTitle: data?.purchaseCode || t("order:form.detail.subTitle"),
        backPath: "/supplier-purchase",
        breadcrumbs: [
          { label: t("order:breadcrumb.home"), path: "/" },
          { label: t("order:breadcrumb.purchase"), path: "/supplier-purchase" },
          { label: data?.purchaseCode || t("order:form.detail.title") },
        ],
      }
    }
    return {
      title: t("order:form.default.title"),
      subTitle: t("order:form.default.subTitle"),
      backPath: "/supplier-purchase",
      breadcrumbs: [
        { label: t("order:breadcrumb.home"), path: "/" },
        { label: t("order:breadcrumb.purchase"), path: "/supplier-purchase" },
        { label: t("order:form.detail.title") },
      ],
    }
  }

  const defaultPageConfig = getPageConfig()
  const pageConfig = backPath ? { ...defaultPageConfig, backPath } : defaultPageConfig

  // Initial form data
  const getFormInitialData = (): IOrderFormData => {
    const initData = getInitData()

    if (mode === "create") {
      // Pre-fill partner fields from preselectedPartner (the contracted-partner flow)
      if (preselectedPartner) {
        return {
          ...initData,
          deliveryType: "DEFAULT_DATE",
          partnerName: preselectedPartner.profileName ?? preselectedPartner.partnerName,
          regNumber: preselectedPartner.regNumber,
          supplierId: preselectedPartner.id,
          partnerType: preselectedPartner.partner?.type,
          // Buyer side: receiver = self (own staff/branch), so leave empty for the user to pick
          // Supplier side: receiver = the preselected partner — payload will pull from businessData
        }
      }
      return {
        ...initData,
        deliveryType: "DEFAULT_DATE",
      }
    }

    if (mode === "edit" && data) {
      return {
        ...initData,
        ...data,
        // B37: a scheduled order keeps its current day selected until the buyer picks another.
        deliveryType: data.deliveryType || "DEFAULT_DATE",
        deliveryCheck: (data.deliveryType || "DEFAULT_DATE") === "DEFAULT_DATE" && data.deliveryDate ? String(data.deliveryDate) : undefined,
        partnerName: data.receiverBusiness?.profileName,
        regNumber: data.receiverBusiness?.regNumber,
        supplierId: data.receiverBusiness?.id,
        types: false,
      }
    }

    return initData
  }

  // Business change handler
  const handleBusinessChange = async (id: string, setFieldValue: any) => {
    if (id) {
      await fetchBusiness(id, setFieldValue)
      if (mode === "edit") {
        setFieldValue("types", true)
      }
    }
  }

  // Validation helpers
  const validateBeforeSubmit = (value: IOrderFormData): boolean => {
    const checkBusinessData = mode === "edit" && value.types ? businessData : (mode === "edit" ? data : businessData)

    if (!value?.lines?.length) {
      message.error(helpText(`${side === "supplier" ? "order.supplier-sales-new" : "order.buyer-sales-new"}.validation.items`) ?? t("order:validation.noLines"))
      return false
    }

    if (!checkBusinessData?.supplierStaff) {
      message.error(t("order:validation.noSupplierStaff"))
      return false
    }

    if (!checkBusinessData?.buyerStaff) {
      message.error(t("order:validation.noBuyerStaff"))
      return false
    }

    // 2+ attached terms require an explicit pick (no auto pre-fill). 1/0 falls back.
    const paymentTerms = checkBusinessData?.paymentTerms ?? []
    if (paymentTerms.length >= 2 && !value?.paymentTermId) {
      message.error(t("order:validation.noPaymentTerm"))
      return false
    }

    if (mode === "create") {
      // Buyer side: user selects their own receiver — read from form values
      if (side === "buyer") {
        if (!value?.receiverStaffId) {
          message.error(t("order:validation.noReceiverStaff"))
          return false
        }
        if (value?.receiverBranchId === undefined) {
          message.error(t("order:validation.noBranch"))
          return false
        }
      } else {
        // Supplier side: receiver comes from the preselected/searched partner
        if (!businessData?.receiverStaff) {
          message.error(t("order:validation.noReceiverStaff"))
          return false
        }
        if (businessData?.receiverBranchId === undefined && !businessData?.receiverBranch?.id) {
          message.error(t("order:validation.noBranch"))
          return false
        }
      }
    }

    if (mode === "edit") {
      if (value.types && !businessData?.receiverStaff) {
        message.error(t("order:validation.noReceiverStaff"))
        return false
      }
      if (!value.types && !data?.receiverStaffId) {
        message.error(t("order:validation.noReceiverStaff"))
        return false
      }
      if (value.types && businessData?.receiverBranchId === undefined) {
        message.error(t("order:validation.noBranch"))
        return false
      }
      if (!value.types && value?.receiverBranchId === undefined) {
        message.error(t("order:validation.noBranch"))
        return false
      }
    }

    return true
  }

  // Build payload for API
  const buildPayload = (value: IOrderFormData, isSend: boolean) => {
    const attachments = (value?.attachments || []).map((item: any) => ({
      description: item?.description,
      name: item?.name,
      url: item?.url,
    }))

    const lines = (value?.lines || []).map((item: any) => ({
      variantId: item?.variantId || item?.id,
      parentVariantId: item?.variant?.id || item?.variantParentId || item?.variantId || item?.id,
      warehouseId: item?.warehouseId,
      quantity: item?.quantity,
      price: item?.price,
      discountType: item?.discountType,
      discountValue: item?.discountValue,
    }))

    if (mode === "create") {
      const additionalLines = (value?.additionalLines || []).map((item: any) => ({
        name: item?.name,
        unit: item?.unit,
        price: item?.price,
        quantity: item?.quantity,
        discountType: item?.discountType,
        discountValue: item?.discountValue,
      }))

      // Buyer side picks their own receiver branch/staff via the form;
      // supplier side reads them off the (preselected or searched) partner.
      const isBuyerCreate = side === "buyer"
      const receiverBranchId = isBuyerCreate ? value?.receiverBranchId : businessData?.receiverBranch?.id
      const receiverStaffId = isBuyerCreate ? value?.receiverStaffId : businessData?.receiverStaff?.id

      // No pre-fill (B4): 2+ terms -> buyer's pick; 1/0 -> the single attached term id,
      // falling back to the network default (paymentTermId is required by create.ts).
      const paymentTermId = value?.paymentTermId || businessData?.paymentTerms?.[0]?.id || businessData?.paymentTermId

      return {
        businessId: businessData?.id,
        receiverBranchId,
        paymentTermId,
        deliveryType: value?.deliveryType || "DEFAULT_DATE",
        deliveryDate:
          value?.deliveryType === "DEFAULT_DATE"
            ? dayjs(value?.deliveryCheck).format()
            : dayjs(value?.deliveryDate).format(),
        receiverStaffId,
        senderNote: value?.senderNote || "",
        senderAdditionalNote: value?.senderAdditionalNote || "",
        shippingAmount: isBuyerCreate ? "" : value?.shippingAmount,
        discountType: isBuyerCreate ? "" : value?.discountType,
        discountValue: isBuyerCreate ? "" : value?.discountValue,
        toReview: false,
        send: isSend,
        attachments,
        lines,
        additionalLines: isBuyerCreate ? [] : additionalLines,
      }
    }

    // Edit mode payload
    const editBusinessId = value?.types ? businessData?.id : value?.receiverBusinessId
    // Keep the existing term unless the buyer re-picked one (draft edit). Falls back to the
    // single/default attached term when a partner was re-selected.
    const editPaymentTermId = value?.paymentTermId || businessData?.paymentTerms?.[0]?.id || businessData?.paymentTermId

    return {
      businessId: editBusinessId,
      paymentTermId: editPaymentTermId,
      // B37: send the type the buyer picked; a scheduled day comes from the day cards
      // (deliveryCheck), falling back to the order's current date when none was re-picked.
      deliveryType: value?.deliveryType || "DEFAULT_DATE",
      deliveryDate:
        value?.deliveryType === "CUSTOM_DATE"
          ? dayjs(value?.deliveryDate).format()
          : value?.deliveryCheck
            ? dayjs(value.deliveryCheck).format()
            : data?.deliveryDate
              ? dayjs(data.deliveryDate).format()
              : "",
      receiverBranchId: value?.receiverBranchId,
      receiverStaffId: value?.receiverStaffId,
      senderNote: value?.senderNote || "",
      senderAdditionalNote: value?.senderAdditionalNote || "",
      shippingAmount: "",
      discountType: "",
      discountValue: "",
      toReview: false,
      send: isSend,
      attachments,
      lines,
    }
  }

  // Submit handler
  const handleSubmit = async (value: IOrderFormData, action: "save" | "send") => {
    setLoading(true)

    if (!validateBeforeSubmit(value)) {
      setLoading(false)
      return
    }

    try {
      const isSend = action === "send"
      const payload = buildPayload(value, isSend)

      if (mode === "create") {
        await OrderApi.create(payload as any)
        // Past-tense title + next step (R8, @dehub/help); the locale message stays as the fallback.
        nextStepToast(isSend ? `${helpNs}.toast.send` : `${helpNs}.toast.save`, {
          fallback: { title: isSend ? t("order:submit.sentSuccess") : t("order:submit.savedSuccess") },
        })
      } else if (mode === "edit" && data?.id) {
        await OrderApi.update(data.id, payload as any)
        if (side === "buyer") {
          // Past-tense title + next step (R8, @dehub/help); the locale message stays as the fallback.
          nextStepToast(isSend ? `${helpNs}.toast.edit-send` : `${helpNs}.toast.edit-save`, {
            fallback: { title: isSend ? t("order:submit.editSentSuccess") : t("order:submit.editSavedSuccess") },
          })
        } else {
          message.success(isSend ? t("order:submit.editSentSuccess") : t("order:submit.editSavedSuccess"))
        }
      }

      if (onSuccess) {
        onSuccess()
      } else {
        navigate(pageConfig.backPath)
      }
    } catch (err) {
      const errMsg = (err as HttpHandler)?.message || ""
      // Buyer side: backend keys (error.order.zero_price_line, ...) live in the errors namespace.
      message.error(side === "buyer" ? translateBackendError(err) : t(`order:errors.${errMsg}`, { defaultValue: errMsg }))
    }

    setLoading(false)
  }

  // Detail action handler
  const handleDetailAction = (action: string) => {
    if (action === "review") {
      setModalAction("review")
    } else if (action === "approve") {
      setModalAction("detail-confirm")
    } else if (action === "reject") {
      setModalAction("detail-reject")
    }
  }

  // Staff select item renderer
  const SelectItem = React.forwardRef<HTMLDivElement, any>(({ item, ...others }, ref) => (
    <Box ref={ref} {...others}>
      <Group>
        <Avatar src={item?.avatar || null}>
          <UserIcon size={16} />
        </Avatar>
        <Box>
          <Text size="sm" fw={500} c="var(--mantine-color-dark-9)">
            {item?.lastName}
          </Text>
          <Text size="sm" fw={500} c="var(--mantine-color-dark-5)">
            {item?.firstName}
          </Text>
        </Box>
      </Group>
    </Box>
  ))

  // Get display data based on mode
  const getDisplayData = (values: any) => {
    if (mode === "detail") return data
    if (mode === "edit" && !values?.types) {
      // B34: the order JSON carries the supplier as a nested business (logics/order/get.ts),
      // while the ledger reads partner / refCode / profileName / regNumber at the top level.
      // A buyer's PURCHASE order goes to its receiver; a SALES order comes from its sender.
      const counterpart = data?.type === "SALES" ? data?.senderBusiness : data?.receiverBusiness
      return counterpart
        ? {
          ...data,
          partner: counterpart.partner,
          refCode: counterpart.refCode,
          profileName: counterpart.profileName,
          regNumber: counterpart.regNumber,
        }
        : data
    }
    return businessData
  }

  const schema = getValidationSchema(mode, side)
  const initialData = getFormInitialData()

  const form = useForm<IOrderFormData>({
    mode: "controlled",
    initialValues: {
      ...initialData,
      lines: initialData?.lines || [],
      attachments: initialData?.attachments || [],
    },
    validate: yupResolver(schema) as any,
  })

  const { values, setFieldValue, errors } = form

  // @dehub/help (spec 10 §2.9): supplier → order.supplier-sales-new, buyer → order.buyer-sales-new.
  // The buyer form leaves `side` unset so the BUYER status rows apply (a SENDER side would tie with the supplier rows).
  const helpNs = side === "supplier" ? "order.supplier-sales-new" : "order.buyer-sales-new"
  const { set: setHelpContext } = useHelpContext()
  useEffect(() => { setHelpContext({ side: side === "supplier" ? "SENDER" : undefined, status: data?.orderStatus }) }, [side, data?.orderStatus, setHelpContext])
  const dayDone = values?.deliveryType === "DEFAULT_DATE" ? !!values?.deliveryCheck : values?.deliveryType === "CUSTOM_DATE" ? !!values?.deliveryDate : false
  const checklistItems = useMemo(() => [
    { id: "cust", done: !!values?.supplierId, target: "cust-box" },
    { id: "items", done: (values?.lines?.length ?? 0) > 0, target: "items-card" },
    { id: "day", done: dayDone, target: "days" },
    // Хүргэлтийн төлбөр is optional (user 2026-11-04): not part of the send checklist.
  ], [values?.supplierId, values?.lines?.length, dayDone])
  const checklistMissing = useChecklistMissing()
  const sendBlockedByChecklist = mode === "create" && side === "supplier" && checklistMissing.length > 0

  // ── Pricing delegation ───────────────────────────────────────────────────
  const [drawerOpened, setDrawerOpened] = useState(false)

  const currentOrderStatus = data?.orderStatus
  const isReadonly = mode === "detail"
  // B35: a DRAFT (and a REVIEW_REJECTED order sent back for changes) is re-priced too,
  // exactly as the server lets the sender edit it (routes/api/order/update.ts).
  const isDraftEditable = !isReadonly && (!currentOrderStatus || ["DRAFT", "REGISTERED", "REVIEW_REJECTED"].includes(currentOrderStatus))

  const priceLines = useMemo(
    () => (values.lines || []).map((l: any) => ({ variantId: l.variantId, quantity: l.quantity || 1 })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values.lines],
  )

  const sellerBusinessId = (
    side === "supplier"
      ? user?.currentBusinessId
      : values.supplierId
  ) ?? undefined
  const buyerBusId = (
    side === "supplier"
      ? values.supplierId
      : user?.currentBusinessId
  ) ?? undefined

  const { priceByVariant } = useVariantPricing(
    isDraftEditable ? sellerBusinessId : undefined,
    isDraftEditable ? buyerBusId : undefined,
    isDraftEditable ? priceLines : [],
  )

  // Spec §9 cutoff — fetch availability against the SUPPLIER business so the
  // date picker can disable past-cutoff dates and show a banner.
  const cutoffEnabled = !!sellerBusinessId && !isReadonly
  const { data: availability } = useAvailableDeliveryDates(sellerBusinessId, cutoffEnabled)
  const unavailableSet = useMemo(() => {
    const s = new Set<string>()
    for (const d of availability?.dates ?? []) if (!d.available) s.add(d.date)
    return s
  }, [availability])
  const cutoffTriggered = !!availability && !availability.hasOverride && availability.dates.some(
    (d) => d.reasonIfUnavailable === "PAST_CUTOFF_TODAY" || d.reasonIfUnavailable === "PAST_CUTOFF_TOMORROW",
  )

  useEffect(() => {
    if (!isDraftEditable) return
    ;(values.lines || []).forEach((l: any, i: number) => {
      const fresh = priceByVariant[l.variantId]
      if (fresh != null && fresh !== l.price) {
        form.setFieldValue(`lines.${i}.price`, fresh)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceByVariant, isDraftEditable])
  // ─────────────────────────────────────────────────────────────────────────

  // ── Payment-term discount (B.8 / D2) ─────────────────────────────────────
  // Supplier side only: fill the order-level discount ONCE per payment term from
  // that term's rule. We remember exactly what we filled in, so a value the
  // supplier typed — or cleared — is never overwritten; the fill runs again only
  // when the payment term itself changes.
  const autoFilledFor = React.useRef<{ termId: string; type?: string; value?: number } | null>(null)
  const autoFill = autoFilledFor.current
  const isAutoFilledDiscount =
    side === "supplier" &&
    mode === "create" &&
    autoFill != null &&
    autoFill.type != null &&
    values.discountType === autoFill.type &&
    values.discountValue === autoFill.value

  useEffect(() => {
    if (side !== "supplier" || mode !== "create") return
    const termId = values.paymentTermId || businessData?.paymentTerms?.[0]?.id || businessData?.paymentTermId
    if (!termId) return

    const filled = autoFilledFor.current
    if (filled?.termId === termId) return

    // Untouched = never filled and still empty, or still holding our own last fill.
    // `!= null` on purpose: 0 is a legal discount value.
    const untouched =
      (values.discountType == null && values.discountValue == null) ||
      (filled != null && values.discountType === filled.type && values.discountValue === filled.value)
    if (!untouched) return

    const rule = findTermDiscount(businessData, termId)
    if (rule && rule.discountValue != null) {
      setFieldValue("discountType", rule.discountType)
      setFieldValue("discountValue", rule.discountValue)
      autoFilledFor.current = { termId, type: rule.discountType, value: rule.discountValue }
    } else if (filled?.type != null) {
      // The newly selected term has no rule — drop what we filled for the old one
      // instead of silently charging a discount the chosen term does not carry.
      setFieldValue("discountType", undefined)
      setFieldValue("discountValue", undefined)
      autoFilledFor.current = { termId }
    } else {
      autoFilledFor.current = { termId }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [side, businessData, values.paymentTermId])

  const displayData = getDisplayData(values)

  // No `|| ""` terminator: an empty string is not null, and the note renders on
  // `!= null`, so a term whose description never arrived produced the sentence
  // `Auto-filled from the "" payment term rule`. Falling through to undefined
  // hides the note instead.
  const autoFilledTermName = isAutoFilledDiscount
    ? ((businessData?.paymentTerms ?? []).find((x: any) => x?.id === autoFill?.termId)?.description
      || businessData?.paymentTerm?.description
      || undefined)
    : null

  // Buyers have no discount input — de-order-service applies the rule of the term
  // they picked, so the ledger only previews the result (B.2.3).
  const buyerTermDiscount = side === "buyer" ? findTermDiscount(displayData, values.paymentTermId) : null

  // Buyer side only. The copy is written to the party RECEIVING the discount
  // ("Choose X and save Y off the order total"), so on the supplier's own SALES
  // form it told the party granting it to go and save money. The supplier
  // already gets the right wording two panels down, on the `discountAutoFilled`
  // note beside the discount field they actually own.
  const termBanners = side === "buyer"
    ? (displayData?.paymentTerms ?? [])
      .map((x: any) => termDiscountLabel(x, t))
      .filter((x: string | null): x is string => !!x)
    : []

  // B59 / B35: the server refuses to send a 0₮ line (error.order.zero_price_line), so the
  // buyer's send is locked with the reason instead. Promotion gifts are exempt.
  const zeroPriceLines = side === "buyer"
    ? (values.lines || []).filter((l: any) => !(Number(l?.price) > 0) && !(l?.priceGroupName === "FREE_GOODS" && l?.promotionId))
    : []
  const zeroPriceReason = zeroPriceLines.length > 0 ? t("order:buyerOrders.edit.zeroPriceBlocked", { count: zeroPriceLines.length }) : undefined

  // B43: the payment confirmation previews the term the buyer actually picked. Each
  // paymentTerms[] entry carries condition / advancePercent / days (logics/network/get.ts);
  // an older backend without them falls back to the network default only.
  const buyerPaymentData = (() => {
    if (side !== "buyer") return undefined
    const pickedId = values.paymentTermId || businessData?.paymentTerms?.[0]?.id || businessData?.paymentTermId
    if (!pickedId || pickedId === businessData?.paymentTermId) return businessData?.paymentTerm
    const picked = (businessData?.paymentTerms ?? []).find((t: any) => t.id === pickedId)
    return picked?.condition ? picked : null
  })()

  // B39: the buyer's scheduled days come from the supplier's own calendar
  // (available-delivery-dates), weekends and holidays shown disabled; the supplier
  // side keeps its existing day list.
  const todayIso = dayjs().format("YYYY-MM-DD")
  const dayOptions: { date: Date; iso: string; weekday: number; unavailable: boolean }[] =
    side === "buyer" && availability?.dates?.length
      ? availability.dates
        .filter((d) => d.date > todayIso)
        .slice(0, 6)
        .map((d) => ({ date: dayjs(d.date).toDate(), iso: d.date, weekday: dayjs(d.date).day(), unavailable: !d.available }))
      : staticDays.map((item: any) => {
        const iso = dayjs(item.date).format("YYYY-MM-DD")
        return { date: item.date, iso, weekday: item.weekend, unavailable: unavailableSet.has(iso) }
      })
  const pickedDayIso = values.deliveryCheck ? dayjs(values.deliveryCheck).format("YYYY-MM-DD") : null

  const partnerFields = (
    <>
                    <Text className={classes.cardTitle} mb="md">
                      {mode === "edit" ? t("order:section.basicInfo") : t("order:section.partnerInfo")}<HelpIcon helpKey={`${helpNs}.section.customer`} variant="section" />
                    </Text>

                    {/* Partner search — three inputs in one row */}
                    {!isReadOnly && !partnerLocked && (
                      <Box mb="md" data-help-id="cust-box">
                        <Grid gap="sm">
                          {hasNetworkAccess && (
                            <Grid.Col span={searchSpan}>
                              <Select
                                {...form.getInputProps("supplierId")}
                                label={<>{side === "supplier" ? t("order:fields.buyerBusiness") : t("order:fields.supplierBusiness")}<HelpIcon helpKey={`${helpNs}.field.customer-search`} /></>}
                                placeholder={t("common:filter.selectPlaceholder")}
                                data={(networkData?.res || [])
                                  .filter((a: any) => {
                                    if (mode === "edit") {
                                      if (values.regNumber) return a.regNumber === values.regNumber
                                      if (values.partnerName) return a.partnerName.includes(values.partnerName)
                                    }
                                    return true
                                  })
                                  .map((item: IBusiness) => ({
                                    label: item.profileName,
                                    value: item.id,
                                  }))}
                                onChange={(e: string | null) => {
                                  form.setFieldValue("regNumber", networkData?.mappedRes[e!]?.regNumber)
                                  form.setFieldValue("partnerName", networkData?.mappedRes[e!]?.partnerName)
                                  form.setFieldValue("supplierId", e ?? undefined)
                                  if (e) handleBusinessChange(e, form.setFieldValue)
                                }}
                                value={values.supplierId ?? null}
                                rightSection={
                                  <XIcon
                                    size={15}
                                    className={classes.clearIcon}
                                    onClick={() => {
                                      form.setFieldValue("regNumber", undefined)
                                      form.setFieldValue("partnerName", undefined)
                                      form.setFieldValue("supplierId", undefined)
                                    }}
                                  />
                                }
                                required
                              />
                            </Grid.Col>
                          )}

                          {/* Tax number search */}
                          <Grid.Col span={searchSpan}>
                            <TextInput
                              {...form.getInputProps("regNumber")}
                              label={t("order:fields.searchByTaxNumber")}
                              placeholder={t("order:fields.taxNumberPlaceholder")}
                              onChange={(e) => {
                                form.getInputProps("regNumber").onChange(e)
                                if (mode === "edit") {
                                  form.setFieldValue("partnerName", undefined)
                                  form.setFieldValue("supplierId", undefined)
                                }
                              }}
                              rightSection={<MagnifyingGlassIcon size={16} />}
                              required
                            />
                          </Grid.Col>

                          {/* Partner name search */}
                          <Grid.Col span={searchSpan}>
                            <TextInput
                              {...form.getInputProps("partnerName")}
                              label={t("order:fields.searchByPartnerName")}
                              placeholder={t("order:fields.partnerNamePlaceholder")}
                              onChange={(e) => {
                                form.getInputProps("partnerName").onChange(e)
                                if (mode === "edit") {
                                  form.setFieldValue("regNumber", undefined)
                                  form.setFieldValue("supplierId", undefined)
                                }
                              }}
                              rightSection={
                                <XIcon
                                  size={15}
                                  className={classes.clearIcon}
                                  onClick={() => {
                                    form.setFieldValue("partnerName", undefined)
                                    form.setFieldValue("supplierId", undefined)
                                    form.setFieldValue("regNumber", undefined)
                                  }}
                                />
                              }
                              required
                            />
                          </Grid.Col>
                        </Grid>
                      </Box>
                    )}

                    {/* The locked partner has no inputs, so a validation error on its fields (a missing or malformed
                        tax number, no name) would otherwise sit on a hidden field and make saving silently impossible. */}
                    {partnerLocked && [errors?.regNumber, errors?.partnerName, errors?.supplierId].filter(Boolean).length > 0 && (
                      <Alert color="red" variant="light" mb="md">
                        {[errors?.regNumber, errors?.partnerName, errors?.supplierId].filter(Boolean).join(". ")}
                      </Alert>
                    )}

                    {/* Display fields — three columns, sentence-case label over a bold value */}
                    <Grid gap="md">
                      <Grid.Col span={fieldSpan}>
                        <FormField
                          label={t("order:fields.partnerName")}
                          value={joinCodeName(displayData?.partner?.refCode, displayData?.partner?.businessName)}
                        />
                      </Grid.Col>

                      <Grid.Col span={fieldSpan}>
                        <FormField
                          label={side === "supplier" ? t("order:fields.clientName") : t("order:fields.supplierName")}
                          value={joinCodeName(displayData?.refCode, displayData?.profileName || displayData?.partnerName)}
                        />
                      </Grid.Col>

                      <Grid.Col span={fieldSpan}>
                        <FormField
                          label={t("order:fields.taxNumber")}
                          value={displayData?.regNumber || "-"}
                        />
                      </Grid.Col>

                      {/* B.2.3 — discount banner, directly above the payment-term field.
                          Visible as soon as any attached term carries a rule, even before
                          a term is picked. */}
                      {termBanners.length > 0 && (
                        <Grid.Col span={12}>
                          <Flex align="flex-start" gap="xs" p="xs" bg="teal.0" bdrs="md" bd="1px solid var(--mantine-color-teal-2)">
                            <PercentIcon size={14} color="var(--mantine-color-teal-7)" />
                            <Text size="sm" fw={500} c="teal.8">
                              {termBanners.join(" · ")}
                            </Text>
                          </Flex>
                        </Grid.Col>
                      )}

                      {(() => {
                        // Network with 2+ attached terms -> buyer picks (description only,
                        // no condition/configType groups). 1 or 0 -> read-only, auto-applied.
                        // Only selectable while the order is a draft (B6).
                        const terms = displayData?.paymentTerms ?? []
                        const selectable = terms.length >= 2 && isDraftEditable
                        return selectable ? (
                          <Grid.Col span={fieldSpan}>
                            <SelectItemField
                              value={values.paymentTermId ?? null}
                              onChange={(v) => form.setFieldValue("paymentTermId", v ?? undefined)}
                              error={errors?.paymentTermId}
                              label={<>{t("order:fields.paymentTerm")}<HelpIcon helpKey={`${helpNs}.field.payment-term-select`} /></>}
                              placeholder={t("common:filter.selectPlaceholder")}
                              options={terms.map((x: any) => ({ value: x.id, label: x.description }))}
                              required
                            />
                          </Grid.Col>
                        ) : (
                          <Grid.Col span={fieldSpan}>
                            <FormField
                              label={<>{t("order:fields.paymentTerm")}<HelpIcon helpKey={`${helpNs}.field.payment-term`} /></>}
                              value={terms[0]?.description || displayData?.paymentTerm?.description || "-"}
                            />
                          </Grid.Col>
                        )
                      })()}

                      <Grid.Col span={fieldSpan}>
                        <FormField
                          label={t("order:fields.buyerManager")}
                          value={renderName(displayData?.buyerStaff)}
                        />
                      </Grid.Col>

                      <Grid.Col span={fieldSpan}>
                        <FormField
                          label={t("order:fields.supplierManager")}
                          value={renderName(displayData?.supplierStaff)}
                        />
                      </Grid.Col>

                      {/* Staff/Branch Selection - buyer edit mode only */}
                      {showStaffBranchFields && (
                        <>
                          <Grid.Col span={fieldSpan}>
                            <SelectItemField
                              value={values.receiverStaffId}
                              onChange={(v) => form.setFieldValue("receiverStaffId", v ?? undefined)}
                              error={errors?.receiverStaffId}
                              label={t("order:fields.receiverStaff")}
                              placeholder={t("common:filter.selectPlaceholder")}
                              options={(staffData || []).map((item: any) => ({
                                label: renderName(item),
                                value: item?.id,
                                item,
                              }))}
                              itemComponent={SelectItem}
                              filter={(value: any, item: any) =>
                                item.label.toLowerCase().includes(value.toLowerCase().trim())
                              }
                              required
                            />
                          </Grid.Col>

                          <Grid.Col span={fieldSpan}>
                            <Select
                              {...form.getInputProps("receiverBranchId")}
                              label={<>{t("order:fields.receiverBranch")}<HelpIcon helpKey={`${helpNs}.field.receiver`} /></>}
                              placeholder={t("common:filter.selectPlaceholder")}
                              data={(branchData || []).map((item: any) => ({
                                label: item?.name,
                                value: item?.id,
                              }))}
                              onChange={(receiveBranch) => {
                                form.setFieldValue("receiverBranchId", receiveBranch ?? undefined)
                                if (receiveBranch) fetchBranch(receiveBranch)
                              }}
                              value={values.receiverBranchId ?? null}
                              required
                            />
                          </Grid.Col>
                        </>
                      )}

                      {/* Display staff/branch for non-edit modes */}
                      {!showStaffBranchFields && (
                        <>
                          <Grid.Col span={fieldSpan}>
                            <FormField
                              label={t("order:fields.receiverStaff")}
                              value={renderName(displayData?.receiverStaff)}
                            />
                          </Grid.Col>

                          <Grid.Col span={fieldSpan}>
                            <FormField
                              label={<>{t("order:fields.receiverBranch")}<HelpIcon helpKey={`${helpNs}.field.receiver`} /></>}
                              value={displayData?.receiverBranch?.name || "-"}
                            />
                          </Grid.Col>
                        </>
                      )}

                      <Grid.Col span={fieldSpan}>
                        <FormField
                          label={t("order:fields.receiverAddress")}
                          value={
                            mode === "edit" && receiverBranch
                              ? receiverBranch[0]?.branchAddress || "-"
                              : displayData?.receiverBranch?.branchAddress || "-"
                          }
                        />
                      </Grid.Col>

                      {/* Delivery display for detail mode */}
                      {isReadOnly && (
                        <Grid.Col span={fieldSpan}>
                          <FormField
                            label={t("order:fields.deliveryType")}
                            value={data?.deliveryType === "CUSTOM_DATE" ? t("order:fields.customDate") : t("order:fields.scheduledDate")}
                          />
                        </Grid.Col>
                      )}

                      {/* VAT field for create mode (Ord_SalesNew «НӨАТ төлөгч») */}
                      {mode === "create" && (
                        <Grid.Col span={fieldSpan}>
                          <FormField
                            label={t("order:fields.vatPayer")}
                            value={displayData?.isVatPayer == null ? "-" : displayData.isVatPayer ? t("common:actions.yes") : t("common:actions.no")}
                          />
                        </Grid.Col>
                      )}
                    </Grid>
    </>
  )

  const deliveryFields = (
    <>
                          <Box>
                            <Text className={classes.subLabel} mb="sm">{t("order:fields.deliveryType")}<HelpIcon helpKey={`${helpNs}.field.delivery-type`} /></Text>
                            {supplierCreate ? (
                              <SegmentedControl
                                value={values?.deliveryType ?? ""}
                                onChange={(v) => form.setFieldValue("deliveryType", v)}
                                // Canvas order: «Хуваарьт өдөр» first, then «Өөр өдөр сонгох».
                                data={[...(deliveryDateTypes || [])]
                                  .sort((a: any, b: any) => Number(b?.code === "DEFAULT_DATE") - Number(a?.code === "DEFAULT_DATE"))
                                  .map((item: any) => ({ value: item?.code, label: item?.name }))}
                                color="dark"
                              />
                            ) : (
                            <Radio.Group
                              value={values?.deliveryType ?? ""}
                              onChange={(v) => form.setFieldValue("deliveryType", v)}
                            >
                              <Group gap="lg">
                                {(deliveryDateTypes || []).map((item: any) => (
                                  <Radio key={item?.code} value={item?.code} label={item?.name} color="dark" />
                                ))}
                              </Group>
                            </Radio.Group>
                            )}
                          </Box>

                          {/* Cutoff notice, then the day tiles / custom date picker */}
                          <Box mt="md">
                            {cutoffTriggered && (
                              <Alert color="yellow" variant="light" mb="sm" radius="md">
                                {t("order:fields.cutoffPassedBanner", {
                                  defaultValue:
                                    "Өдрийн 14:00 цагаас хойш захиалга үүсгэж байна. Маргааш хүргэх боломжгүй, нөгөөдрөөс сонгоно уу.",
                                })}
                              </Alert>
                            )}
                            {values?.deliveryType === "DEFAULT_DATE" ? (
                              <Input.Wrapper
                                label={t("order:fields.nearestDeliveryDate")}
                                withAsterisk
                                error={!values?.deliveryCheck && errors?.deliveryCheck}
                              >
                                <SimpleGrid cols={3} spacing="xs" mt="xs">
                                  {dayOptions.map((item, index: number) => {
                                    const weekdayKey = [
                                      "sunday",
                                      "monday",
                                      "tuesday",
                                      "wednesday",
                                      "thursday",
                                      "friday",
                                      "saturday",
                                    ][item.weekday] || "monday"
                                    // Buyer: the picked day is matched by date (an edited order opens on its own day).
                                    const isActive = side === "buyer" ? pickedDayIso === item.iso : active === index
                                    const isUnavailable = item.unavailable
                                    return (
                                      <UnstyledButton
                                        key={index}
                                        className={classes.dayCard}
                                        data-active={isActive || undefined}
                                        data-disabled={isUnavailable || undefined}
                                        disabled={isUnavailable}
                                        onClick={() => {
                                          if (isUnavailable) return
                                          setFieldValue("deliveryCheck", item.date.toString())
                                          setActive(index)
                                        }}
                                      >
                                        <Text className={classes.dayDate}>{dateFormat(item?.date)}</Text>
                                        <Text className={classes.dayWeekday}>
                                          {t(`order:weekdays.${weekdayKey}`)}
                                        </Text>
                                      </UnstyledButton>
                                    )
                                  })}
                                </SimpleGrid>
                              </Input.Wrapper>
                            ) : (
                              <DatePickerInput
                                {...form.getInputProps("deliveryDate")}
                                placeholder={t("order:fields.selectDate")}
                                label={t("order:fields.nearestDeliveryDate")}
                                value={values.deliveryDate ? new Date(values.deliveryDate) : null}
                                onChange={(val) => form.setFieldValue("deliveryDate", val ? dayjs(val).format() : undefined)}
                                excludeDate={(d) => unavailableSet.has(dayjs(d).format("YYYY-MM-DD"))}
                                required
                              />
                            )}
                          </Box>
    </>
  )

  return (
    <>
      <Box p={0} className={classes.root} data-full-height={!hideLayout || undefined}>
              {/* Breadcrumb — hidden when parent provides layout */}
              {!hideLayout && (
                <Box mb="md">
                  <Breadcrumb items={pageConfig.breadcrumbs.map((b) => ({ label: b.label, path: b.path }))} />
                </Box>
              )}

              {/* Header — title + status pill, one subtitle line («… · Бүртгэсэн X · Огноо Y»), actions on the right */}
              {!hideLayout && (
                <Flex justify="space-between" align="flex-end" mb="md" wrap="wrap" gap="md">
                  <Box>
                    <Group gap="sm">
                      <Title order={2} className={classes.pageTitle}>
                        {pageConfig.title}
                      </Title>
                      {side === "buyer" ? (
                        mode === "create" ? <DotPill tone="grey">{t("header.draftStatus")}</DotPill> : data && <OrderStatusPill order={data} />
                      ) : mode === "create" ? (
                        <StatusBadge color="var(--mantine-color-gray-6)" label={t("header.draftStatus")} />
                      ) : (
                        status && <StatusBadge status={status} />
                      )}
                    </Group>
                    <Text fz={14} c="gray.7" mt={4}>
                      {[
                        pageConfig.subTitle,
                        `${t("header.registeredBy")} ${renderName(mode === "create" ? user : data?.orderedUser) || "-"}`,
                        `${t("header.date")} ${dateTimeSecFormat(mode === "create" ? new Date() : data?.createdAt)}`,
                      ].join(" · ")}
                    </Text>
                  </Box>

                  {/* Action Buttons — create: Хадгалах + Илгээх only; edit/detail keep Буцах (OrdB_OrderEdit) */}
                  <Group gap="sm">
                    {(mode !== "create" || supplierCreate) && (
                      <Button variant="default" onClick={() => onCancel?.() || navigate(pageConfig.backPath)}>
                        {t("order:form.back")}
                      </Button>
                    )}
                    {mode === "detail" && (
                      <DetailActions data={data!} onAction={handleDetailAction} />
                    )}
                    {mode === "create" && (
                      <>
                        <FormChecklist helpKey={`${helpNs}.checklist.main`} items={checklistItems} />
                        <HelpButtonGroup helpKey={`${helpNs}.action.save`}>
                          <PermissionButton
                            data-help-id="action-save"
                            permission="ORD_SAVE"
                            variant="default"
                            loading={loading}
                            onClick={() => {
                              const { hasErrors } = form.validate()
                              if (!hasErrors) handleSubmit(form.values, "save")
                            }}
                          >
                            {t("order:form.save")}
                          </PermissionButton>
                        </HelpButtonGroup>
                        {/* Send: missing-items list with «Харуулах» spotlight (R4); permission stays on PermissionButton */}
                        <DisabledReason helpKey={`${helpNs}.reason.send`} when={sendBlockedByChecklist} missing={checklistMissing}>
                          <HelpButtonGroup helpKey={`${helpNs}.action.send`}>
                            <PermissionButton
                              data-help-id="action-send"
                              permission="ORD_SEND"
                              disabledReason={
                                sendBlockedByChecklist
                                  ? undefined
                                  : !values.supplierId || !(values.lines?.length > 0)
                                    ? side === "buyer" ? t("order:buyerOrders.edit.sendNeedsSupplierAndLines") : t("order:form.sendNeedsBusinessAndLines")
                                    : zeroPriceReason
                              }
                              loading={loading}
                              onClick={() => {
                                const { hasErrors } = form.validate()
                                if (!hasErrors) handleSubmit(form.values, "send")
                              }}
                            >
                              {t("order:form.send")}
                            </PermissionButton>
                          </HelpButtonGroup>
                        </DisabledReason>
                      </>
                    )}
                    {mode === "edit" && (
                      <>
                        <Button
                          variant="default"
                          loading={loading}
                          onClick={() => {
                            const { hasErrors } = form.validate()
                            if (!hasErrors) handleSubmit(form.values, "save")
                          }}
                        >
                          {t("order:form.save")}
                        </Button>
                        <ReasonTooltip reason={zeroPriceReason}>
                          <Button
                            leftSection={<PaperPlaneTiltIcon size={16} />}
                            loading={loading}
                            onClick={() => {
                              const { hasErrors } = form.validate()
                              if (!hasErrors) handleSubmit(form.values, "send")
                            }}
                          >
                            {t("order:form.sendForReview")}
                          </Button>
                        </ReasonTooltip>
                      </>
                    )}
                  </Group>
                </Flex>
              )}

              {supplierCreate ? (
                <Grid gap="md" align="flex-start">
                  <Grid.Col span={{ base: 12, lg: 4 }}>
                    <Paper id="order-section-partner" className={classes.card} p={24} mb="md">
                      {partnerFields}
                    </Paper>
                    <Paper id="order-section-delivery" className={classes.card} p={24} mb="md" data-help-id="days">
                      <Text className={classes.cardTitle} mb="md">{t("order:section.delivery")}<HelpIcon helpKey={`${helpNs}.section.delivery`} variant="section" /></Text>
                      {deliveryFields}
                    </Paper>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, lg: 8 }}>
                    <Box id="order-section-lines" data-help-id="items-card">
                      <SupplierSalesAddDynamicForm
                        compact
                        values={values}
                        setFieldValue={setFieldValue}
                        errors={errors}
                        side={side}
                        paymentData={buyerPaymentData}
                        termDiscount={buyerTermDiscount}
                        autoFilledTermName={autoFilledTermName}
                      />
                    </Box>
                  </Grid.Col>
                </Grid>
              ) : (
              <>
              {/* Customer card (canvas): partner search + ledger on the left, delivery on the right column */}
              <Paper id="order-section-partner" className={classes.card} p={24} mb="md">
              <Grid gap="xl" align="flex-start">
              <Grid.Col span={{ base: 12, lg: isReadOnly ? 12 : 8 }}>
                {partnerFields}
              </Grid.Col>

              {/* Delivery — right column of the same card (OrdB_SupplierOrder / OrdB_OrderEdit) */}
              {!isReadOnly && (
                <Grid.Col span={{ base: 12, lg: 4 }} id="order-section-delivery" data-help-id="days">
                  {deliveryFields}
                </Grid.Col>
              )}
              </Grid>
              </Paper>
              </>
              )}

              {/* Buyer edit (canvas OrdB_OrderEdit): lines, summary and attachments are their own cards */}
              {mode === "edit" && (
                <BuyerSalesDynamicForm
                  action={["edit", data]}
                  values={values}
                  setFieldValue={setFieldValue}
                  termDiscount={data?.discountType ? { discountType: data.discountType, discountValue: Number((data as any)?.discountValue) || 0 } : null}
                  zeroPriceCount={zeroPriceLines.length}
                />
              )}

              {/* Create: the lines, summary, notes and attachments are their own cards inside
                  SupplierSalesAddDynamicForm — no extra collapsible wrapper around them. */}
              {mode === "create" && !supplierCreate && (
                <Box id="order-section-lines" data-help-id="items-card">
                  <SupplierSalesAddDynamicForm
                    values={values}
                    setFieldValue={setFieldValue}
                    errors={errors}
                    side={side}
                    paymentData={buyerPaymentData}
                    termDiscount={buyerTermDiscount}
                    autoFilledTermName={autoFilledTermName}
                  />
                </Box>
              )}

              {mode === "detail" && (
                <Paper id="order-section-lines" className={classes.card} p={24} mb="md">
                  <Text className={classes.cardTitle} mb="md">{t("order:section.orderLines")}</Text>
                  <PurchaseSalesDynamicDetailForm action={["detail", data]} />
                </Paper>
              )}
            </Box>

      {/* Variant selector drawer — top-level, gated by isDraftEditable */}
      {isDraftEditable && sellerBusinessId && (
        <VariantSelectorDrawer
          opened={drawerOpened}
          onClose={() => setDrawerOpened(false)}
          businessId={values.supplierId!}
          sellerBusinessId={sellerBusinessId!}
          buyerBusinessId={buyerBusId}
          orderType={side === "supplier" ? "SALES" : "PURCHASE"}
          selectedKeys={(values.lines || []).map((l: any) => `${l.variantId}-${l.warehouseId}`)}
          onAdd={(line) => form.insertListItem("lines", line)}
          onAddAll={(lines) => form.setFieldValue("lines", [...(values.lines || []), ...lines])}
        />
      )}

      {/* Modals for detail mode */}
      {mode === "detail" && (
        <>
          <Modal
            opened={modalAction === "review"}
            onClose={() => setModalAction(null)}
            withCloseButton={false}
            size="600px"
            padding={0}
            centered
            radius="lg"
          >
            <SupplierSalesDynamicReviewForm action={data} setActions={() => setModalAction(null)} />
          </Modal>

          <Modal
            opened={modalAction === "detail-confirm" || modalAction === "detail-reject"}
            onClose={() => setModalAction(null)}
            withCloseButton={false}
            size="600px"
            padding={0}
            centered
            radius="lg"
          >
            <PurchaseSalesConfirmForm
              action={data}
              setActions={() => setModalAction(null)}
              constant={modalAction}
            />
          </Modal>
        </>
      )}
    </>
  )
}

